import React, { useId, useRef } from "react";
import { createPortal } from "react-dom";
import CloseIcon from "@mui/icons-material/Close";

import Button from "../../../../../components/Button";
import { IconButton } from "../../../../../components/IconButton";
import { useEscapeKey, useModalA11y } from "../../../../../hooks";
import { useResponsive } from "../../../../../hooks/useResponsive";
import { formatCurrency } from "../../../../../utils";
import { BREAKPOINTS } from "../../../../../utils/constants";
import { rowActionsOf } from "./actions";
import { DETAIL_BUTTONS } from "./constants";
import { grossTaxOf, rowDateText, rowTitleOf } from "./format";
import { ProgressAmount } from "./ProgressAmount";
import { ProgressDetailText } from "./ProgressDetailText";
import { ProgressLock } from "./ProgressLock";
import {
  DetailGrid,
  DetailItem,
  DetailsActions,
  DetailsBackdrop,
  DetailsDialog,
  DetailsHead,
  DetailsTitle,
  DetailTerm,
  DetailValue,
  Explanation,
  Eyebrow,
} from "./styles";
import { ProgressDetailsProps } from "./types";

const modalElement = document.getElementById("modal-root") as HTMLElement;

export const ProgressDetails: React.FC<ProgressDetailsProps> = ({
  row,
  actions,
  onClose,
}) => {
  const isOpen = row !== null;
  const isSheet = useResponsive(BREAKPOINTS.tablet);
  const containerRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useEscapeKey(onClose, isOpen);
  useModalA11y(isOpen, containerRef);

  if (row === null) {
    return null;
  }

  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  const lockActions = {
    ...actions,
    onOpenExpense: (expenseId: number) => {
      onClose();
      actions.onOpenExpense(expenseId);
    },
  };
  const balanceText =
    row.balanceAfter === null ? "Unchanged" : formatCurrency(row.balanceAfter);
  const rowActions = rowActionsOf(row, actions);
  const cashbackText =
    row.cashbackEarned === null
      ? null
      : `+${formatCurrency(row.cashbackEarned)}`;
  const grossTax = row.transaction ? grossTaxOf(row.transaction) : null;
  const grossTaxText = grossTax && `${grossTax.gross} · ${grossTax.tax}`;
  const actionButtons = DETAIL_BUTTONS.flatMap((button) => {
    const action = rowActions.find(({ id }) => id === button.id);
    if (!action) {
      return [];
    }
    const runAction = () => {
      onClose();
      action.onClick();
    };
    return [
      <Button
        key={button.id}
        type={button.type}
        tone={button.tone}
        onClick={runAction}
      >
        {button.label}
      </Button>,
    ];
  });

  return createPortal(
    <DetailsBackdrop $isSheet={isSheet} onClick={handleBackdropClick}>
      <DetailsDialog
        ref={containerRef}
        $isSheet={isSheet}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <DetailsHead>
          <div>
            <Eyebrow>{rowDateText(row.date)}</Eyebrow>
            <DetailsTitle id={titleId}>{rowTitleOf(row)}</DetailsTitle>
          </div>
          <IconButton onClick={onClose} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </DetailsHead>
        <DetailGrid>
          <DetailItem>
            <DetailTerm>Amount</DetailTerm>
            <DetailValue>
              <ProgressAmount row={row} />
            </DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailTerm>Balance after</DetailTerm>
            <DetailValue>{balanceText}</DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailTerm>Details</DetailTerm>
            <DetailValue>
              <ProgressDetailText row={row} />
            </DetailValue>
          </DetailItem>
          {cashbackText && (
            <DetailItem>
              <DetailTerm>Cashback</DetailTerm>
              <DetailValue>{cashbackText}</DetailValue>
            </DetailItem>
          )}
          {grossTaxText && (
            <DetailItem>
              <DetailTerm>Gross · tax</DetailTerm>
              <DetailValue>{grossTaxText}</DetailValue>
            </DetailItem>
          )}
        </DetailGrid>
        <ProgressLock row={row} actions={lockActions} />
        <Explanation>{row.explanation}</Explanation>
        {actionButtons.length > 0 && (
          <DetailsActions>{actionButtons}</DetailsActions>
        )}
      </DetailsDialog>
    </DetailsBackdrop>,
    modalElement,
  );
};
